import {
  REDIRECT_URI,
  GTM_ID as GTMID,
  SMART_LOOK_KEY,
  GOOGLE_GEOCODE_API_URL,
  MAP_API_KEY
} from 'aster-foundation/core/common/utils/ApiConst';
import { setDataToCookie } from 'aster-foundation/core/common/utils/cookieHelper';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import Script from 'next/script';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import smartlookClient from 'smartlook-client';
import wrapper from '@common/Redux/store';
import { flagsmith, FlagsmithProvider } from '@common/Lib/Flagsmith';
import { v4 as uuidv4 } from 'uuid';

import { useDispatch, useSelector } from 'react-redux';
// to call get zone of user
import { getDefaultLocation } from 'aster-foundation/core/epharmacy/DefaultLocation/redux/actions';
import { addUserAddress, getUserAddress, updateZone } from 'aster-foundation/core/epharmacy/address/redux/actions';
import { ThemeProvider } from 'styled-components';
// Style
import GlobalStyle from '@common/Theme/globalStyle';
import lightTheme from '@common/Theme/lightTheme';
// Fonts

import '@fontsource/mulish';
import '@fontsource/mulish/300.css';
import '@fontsource/mulish/600.css';
import '@fontsource/mulish/700.css';

// SEO
import Head from 'next/head';
import ErrorBoundary from '@common/Component/ErrorBoundary/ErrorBoundary';
import { AuthTokenProvider } from '@common/AuthService';

import {
  setAdditionalZoneData,
  setLiveLocationReducer,
  setLocationItemReducer,
  setUserArea,
  setUserLocationData,
  setUserZoneData,
  setSelectedLocationData
} from '@common/Component/HeaderBelow/redux/reducer';
import NextNProgress from '@common/Component/NProgress/index';
import {
  INVALID_ADDRESS_MESSAGE,
  OUT_OF_DELIVERY_MESSAGE,
  errorHandling,
  expireOldCookies,
  getAddressDetailsFromGeoAPI,
  getConfigurationValue,
  getReferralPageForGA,
  stringifyIfObject,
} from '@common/Utility/common-utils';
import { AE_GLOBAL, INS_ZONE, ONLINE_PHARMACY_ROUTE, LOCALSTORAGE_KEYS, PAGE_NAMES, REGION_SWITCHER_DISALLOWED_URLS } from '@common/Utility/constants';
import { EventEmitter } from '@common/Utility/eventEmitter';
import useIsNotSafari from '@common/Utility/hooks';
import logger from '@common/Utility/logger';

// Region Language Context
import { RegionLanguageProvider } from '@common/Context/RegionLanguageContext';
import { handleRedirects } from '@common/Utility/redirectHelper';
import Loader from '@common/Component/Loader/Loader';
import { loadProdConfigs } from '@common/Utility/loadConfigs';

function App({ Component, pageProps, router, flagsmithState, selectedCountry}) {
  const dispatch = useDispatch();
  const appConfig = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);
  const userZone = useSelector((state) => state?.epharmacy?.getUserZone?.data?.data);
  const guestLogin = useSelector((state) => state?.astercore?.guestAccessToken?.data);
  const userLocation = useSelector((state) => state?.app?.userLocationReducer?.location);
  const zoneData = useSelector((state) => state?.app?.userLocationReducer?.zoneAdditionalData);
  const ref = useRef(null);
  const pageType = getReferralPageForGA(router?.asPath);
  const screenName = router?.pathname;
  const initialRenderAppRef = useRef(true);
  const [isReadyToRender, setIsReadyToRender] = React.useState(
    !(
      process.env.NEXT_PUBLIC_SENTRY_ENV === 'PROD-F' ||
      process.env.NEXT_PUBLIC_SENTRY_ENV === 'PREPROD-F' ||
      process.env.NEXT_PUBLIC_SENTRY_ENV === 'PRODKSA-F'
    ),
  );

  useEffect(() => {
    if (
      process.env.NEXT_PUBLIC_SENTRY_ENV !== 'PROD-F' &&
      process.env.NEXT_PUBLIC_SENTRY_ENV !== 'PREPROD-F' &&
      process.env.NEXT_PUBLIC_SENTRY_ENV !== 'PRODKSA-F'
    )
      return;
    const fetchConfig = async () => {
      await loadProdConfigs(selectedCountry);
      setIsReadyToRender(true);
    };
    fetchConfig();
  }, [selectedCountry]);

  const [locationItem, setLocationItem] = React.useState({});
  const [location, setLocation] = React.useState('Select Your Location');
  let canonicalUrl;
  const domain = REDIRECT_URI;
  const GTM_ID = GTMID;
  const routerPath = router.asPath;
  const productUrl = pageProps?.productDetails?.productUrl;
  if (productUrl) {
    canonicalUrl = domain + ONLINE_PHARMACY_ROUTE + (routerPath === '/' ? '' : productUrl);
  } else {
    canonicalUrl = domain + (routerPath === '/' ? '' : routerPath);
  }

  const zoneConfig = appConfig?.find((item) => item?.name === 'ins_zone');

  const isDisallowedSwitcher = REGION_SWITCHER_DISALLOWED_URLS.some((disallowedPath) => router?.asPath?.split('?')?.[0]?.indexOf(disallowedPath) > -1);

  const setZoneInsCookie = () => {
    const insZoneValue = getConfigurationValue(appConfig, INS_ZONE);
    if (typeof window === 'object' && zoneConfig) {
      setDataToCookie(AE_GLOBAL.INS_ZONE,insZoneValue, 0, false);
    }
  };

  React.useMemo(() => {
    setZoneInsCookie();
  }, [appConfig, userZone]);

  useEffect(() => {
    EventEmitter.subscribe('getUserAddressAPICall', () => getUserAddressfunc());
    EventEmitter.subscribe('updateUserZone', ({ latitude, longitude }) => {
      if (latitude && longitude) {
        updateZonefunc(latitude, longitude);
      }
    });
    // call like this EventEmitter.dispatch('updateUserZone', {latitude: lat, longitude: lng});
    EventEmitter.subscribe('setLocation', (address) => {
      if (address) {
        setLocation(address);
      }
    });
    EventEmitter.subscribe('getLocation', () => {
      getLocation();
    });
    EventEmitter.subscribe('setLocationItem', (item) => {
      if (item) {
        setLocationItem(item);
      }
    });
    EventEmitter.subscribe('getLocationName', ({ latitude, longitude }) => {
      if (latitude && longitude) {
        getLocationName(latitude, longitude);
      }
    });

    return () => {
      EventEmitter.unsubscribe('getUserAddressAPICall', () => getUserAddressfunc());
      EventEmitter.unsubscribe('getLocation', () => getLocation());
      EventEmitter.unsubscribe('setLocation', (address) => setLocation(address));
      EventEmitter.unsubscribe('setLocationItem', (item) => setLocationItem(item));
      EventEmitter.unsubscribe('getLocationName', ({ latitude, longitude }) => {
        getLocationName({ latitude, longitude });
      });
      EventEmitter.unsubscribe('updateUserZone', ({ latitude, longitude }) => {
        updateZonefunc(latitude, longitude);
      });
    };
  }, []);

  useEffect(() => {
    dispatch(setLocationItemReducer(locationItem));
  }, [locationItem]);

  useEffect(() => {
    if (typeof window !== 'undefined' && location) {
      localStorage.setItem('userArea', location);
      dispatch(setUserArea(location));
    }
  }, [location]);

  useEffect(() => {
    if (userZone) {
      localStorage.setItem(AE_GLOBAL.USER_ZONE, [...userZone]);
    }
  }, [userZone]);

  const usePreviousRoute = () => {
    router.events?.on('routeChangeStart', () => {
      const path = router.asPath.split('/');
      ref.current = getReferralPageForGA(path);
    });
    return ref.current;
  };

  useEffect(() => {
    smartlookClient.init(SMART_LOOK_KEY);
  }, []);

  const updateZonefunc = (latitude, longitude) => {
    const params = { lat: latitude, long: longitude };
    const storageLat = userLocation?.lat;
    const storageLng = userLocation?.lng;
    const insZoneValue = getConfigurationValue(appConfig, INS_ZONE);
    if (String(latitude) !== String(storageLat) || String(longitude) !== String(storageLng)) {
      localStorage.setItem(LOCALSTORAGE_KEYS.LATITUDE, latitude);
      localStorage.setItem(LOCALSTORAGE_KEYS.LONGITUDE, longitude);
      dispatch(setUserLocationData({ lat: latitude, lng: longitude }));
      dispatch(
        updateZone({
          params,
          onSuccess: (response) => {
            dispatch(setUserZoneData(response?.data));
            dispatch(setAdditionalZoneData(response?.additionalInfo));
            localStorage.setItem(AE_GLOBAL.USER_ZONE, response?.data);
            expireOldCookies(AE_GLOBAL.USER_ZONE, ONLINE_PHARMACY_ROUTE); 
            setDataToCookie(AE_GLOBAL.USER_ZONE, response?.data, 0, false);   
            if(!!guestLogin && (!router?.asPath.includes(PAGE_NAMES.SP_CHECKOUT) && !router?.asPath?.includes(PAGE_NAMES.CHECKOUT))) {
              EventEmitter.dispatch('fetchCartDetails');        
            }
          },
          onFailure: (err) => {
            errorHandling(err, true, screenName, pageType);
            setDataToCookie(AE_GLOBAL.INS_ZONE,insZoneValue, 0, false);
          },
        }),
      );
    }
  };

  useEffect(() => {
    const insZoneValue = getConfigurationValue(appConfig, INS_ZONE);
    if (typeof window !== 'undefined') {
      if (zoneData) {
        const expressZoneValue = zoneData?.find((item) => item?.express);
        if (expressZoneValue && expressZoneValue.zoneId) {
          localStorage.setItem('expressZone', expressZoneValue?.zoneId);
        }
        setDataToCookie(AE_GLOBAL.INS_ZONE, insZoneValue, 0, false);
      }
    }
  }, [zoneData, appConfig?.length]);

  const getGeocodeResponse = async (latitude, longitude) => {
    return fetch(
      `${GOOGLE_GEOCODE_API_URL}geocode/json?latlng=${latitude},${longitude}&key=${MAP_API_KEY}`,
    )
      .then((response) => response.json())
      .then((response) => {
        return response.results;
      });
  };

  const callDefaultLocation = async () => {
    await dispatch(
      getDefaultLocation({
        onSuccess: async (response) => {
          const defaultAddress = response?.data?.[0]?.latLong;
          if(!localStorage?.getItem('preferredAddress')){
            localStorage.setItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION, defaultAddress);
          }
        },
        onFailure: (err) => {
          errorHandling(err, true, screenName, pageType);
        },
      }),
    );
  };

  const getDefaultAddressFromConfig = async (defaultFlag = false) => {
    await dispatch(
      getDefaultLocation({
        onSuccess: async (response) => {
          const defaultAddress = response?.data?.[0]?.latLong;
          const latLng = defaultAddress?.split(/\s+/)?.join('')?.split(',');
          if(localStorage.getItem('preferredAddress')){
            const preferredAddress = JSON.parse(localStorage.getItem('preferredAddress'));
            const lat = preferredAddress?.latLong?.split(/\s+/)?.join('')?.split(',')?.[0];
            const lng = preferredAddress?.latLong?.split(/\s+/)?.join('')?.split(',')?.[1];
            dispatch(
              setUserLocationData({
                lat,
                lng,
              }),
            );

            dispatch(
              setSelectedLocationData({
                preferredAddress
              }),
            );

            EventEmitter.dispatch('setLocation', `${preferredAddress?.areaName}, ${preferredAddress?.cityName}`);
            EventEmitter.dispatch('setLocationItem', preferredAddress);
            EventEmitter.dispatch('updateUserZone', { latitude: lat, longitude: lng });
            localStorage.setItem('Latitude', lat);
            localStorage.setItem('Longitude', lng);
            localStorage.removeItem('isAddressEmpty');
          } else {
            updateZonefunc(latLng?.[0], latLng?.[1]);
            localStorage.setItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION, defaultAddress);
            if (defaultFlag) {
                setLocation(`${response?.data?.[0]?.areaName}, ${response?.data?.[0]?.cityName}`);
                const responseLatLong = response?.data?.[0]?.latLong?.split(', ');
                dispatch(
                  setLiveLocationReducer({
                    lat: parseFloat(responseLatLong[0]),
                    lng: parseFloat(responseLatLong[1]),
                  }),
                );
            } else {
              // find default location address and set default location as selected
              let defaultAddressData = await getGeocodeResponse(latLng?.[0], latLng?.[1]);
              defaultAddressData = getAddressDetailsFromGeoAPI(defaultAddressData?.[0]);
              setLocationItem(defaultAddressData);
              setLocation(`${defaultAddressData?.areaName}, ${defaultAddressData?.cityName}`);
            }
          }   

        },
        onFailure: (err) => {
          errorHandling(err, true, screenName, pageType);
        },
      }),
    );
  };

  const getLocationName = (latitude, longitude) => {
    fetch(
      `${GOOGLE_GEOCODE_API_URL}geocode/json?latlng=${latitude},${longitude}&key=${MAP_API_KEY}`,
    )
      .then((response) => response.json())
      .then(async (response) => {
        const res = response?.results;
        const locationData = getAddressDetailsFromGeoAPI(res?.[0]);
        if (locationData?.cityName === '') {
          // If Not found proper address
          getDefaultAddressFromConfig();
          toast.info(INVALID_ADDRESS_MESSAGE);
        } else if (locationData?.countryName === 'United Arab Emirates' || locationData?.countryName === 'Saudi Arabia') {
          // if user location is inside UAE
          updateZonefunc(latitude, longitude);
          setLocation(`${locationData.areaName}- ${locationData.cityName}`);
          setLocationItem(res?.[0]);
          // dispatch(setLiveLocationReducer({ lat: parseFloat(latitude), lng: parseFloat(longitude) }));
        } else {
          const lat = parseFloat(localStorage?.getItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION)?.split(', ')[0]);
          const long = parseFloat(localStorage?.getItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION)?.split(', ')[1]);
          dispatch(setLiveLocationReducer({ lat: lat, lng: long }));
          if (!localStorage.getItem('isLocationPopUp') || localStorage.getItem('isLocationPopUp') === 'false') {
            toast.info(OUT_OF_DELIVERY_MESSAGE);
            localStorage.setItem('isLocationPopUp', true);
          }
          if (!userLogin && location === 'Select Your Location') {
            getDefaultAddressFromConfig(true);
          }
        }
      })
      .catch((e) => {
        // console.log(e);
      });
  };

  const getLocation = () => {
    const ua = navigator.userAgent;
    if (ua.indexOf('FBAV') === -1 && ua.indexOf('Instagram') === -1) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            // on Success
            const crd = pos.coords;
            dispatch(setLiveLocationReducer({ lat: parseFloat(crd?.latitude), lng: parseFloat(crd?.longitude) }));
            getLocationName(crd?.latitude, crd?.longitude);
          },
          () => {
            // if denied or error
            getDefaultAddressFromConfig(true);
          },
        );
      } else {
        // browser location is not supported
        getDefaultAddressFromConfig(true);
        toast.info('geolocation is not supported by this device');
      }
    } else {
      getDefaultAddressFromConfig(true);
    }
  };

  const fetchLatestUserAddress = () => { 
    dispatch(
      getUserAddress({
        onSuccess: () => {},
        onFailure: (err) => {
          errorHandling(err, true);
        },
      }),
    );
  };

  const updateUserDefaultAddress = (response) => {
    if (localStorage.getItem(LOCALSTORAGE_KEYS.LATITUDE) && localStorage.getItem(LOCALSTORAGE_KEYS.LONGITUDE)) {
      localStorage.removeItem(LOCALSTORAGE_KEYS.PERSIST_ADD_CHANGE);
      const storageLat = localStorage.getItem(LOCALSTORAGE_KEYS.LATITUDE);
      const storageLong = localStorage.getItem(LOCALSTORAGE_KEYS.LONGITUDE);
      const matchingAddress = response?.data.find((address) => {
        const lat = address?.latLong?.split(/\s+/)?.join('')?.split(',')?.[0];
        const lng = address?.latLong?.split(/\s+/)?.join('')?.split(',')?.[1];
        if (storageLat === lat && storageLong === lng && !address.selected) {
          return address;
        }
      });

      if(!matchingAddress) return;

      matchingAddress.selected = true;
      dispatch(
        addUserAddress({
          params: matchingAddress,
          onSuccess: () => {
           fetchLatestUserAddress();
          },
          onFailure: (err) => {
            errorHandling(err, true);
          },
        }),
      );
    }
  };

  const getUserAddressfunc = async () => {
    if (userLogin) {
      dispatch(
        getUserAddress({
          onSuccess: (response) => {
            updateUserDefaultAddress(response);
          },
          onFailure: (err) => {
            errorHandling(err, true);
          },
        }),
      );
    }

    if (localStorage.getItem(LOCALSTORAGE_KEYS.LATITUDE) && localStorage.getItem(LOCALSTORAGE_KEYS.LONGITUDE)) {
      if (
        localStorage.getItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION)?.split(', ')[0] === localStorage.getItem(LOCALSTORAGE_KEYS.LATITUDE) &&
        localStorage.getItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION)?.split(', ')[1] === localStorage.getItem(LOCALSTORAGE_KEYS.LONGITUDE)
      ) {
        getDefaultAddressFromConfig(true);
      } else {
        const lat = parseFloat(localStorage.getItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION)?.split(', ')[0]);
        const long = parseFloat(localStorage.getItem(LOCALSTORAGE_KEYS.DEFAULT_LOCATION)?.split(', ')[1]);
        dispatch(setLiveLocationReducer({ lat, lng: long }));
        getLocationName(
          localStorage.getItem(LOCALSTORAGE_KEYS.LATITUDE),
          localStorage.getItem(LOCALSTORAGE_KEYS.LONGITUDE),
        );
      }
    } else {
      getLocation();
    }
  };

  const guestLoginDep = React.useMemo(() => {
    return guestLogin;
  }, [guestLogin]);
  const userLoginDep = React.useMemo(() => {
    return userLogin;
  }, [userLogin]);

  useEffect(() => {
    if ((userLoginDep || guestLoginDep) && initialRenderAppRef.current) {
      initialRenderAppRef.current = false
      callDefaultLocation();
      getUserAddressfunc();
    }
  }, [guestLoginDep, userLoginDep]);

  const isNotSafari = useIsNotSafari();

  return (
    <React.StrictMode>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <ThemeProvider theme={lightTheme}>
        <RegionLanguageProvider>
          <AuthTokenProvider>
            <ErrorBoundary history={usePreviousRoute()}>
              <FlagsmithProvider
                flagsmith={flagsmith}
                serverState={flagsmithState}
                options={{
                  environmentID: process.env.NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT,
                  traits: { country: router?.asPath?.includes('/sa-') ? 'SA' : 'AE' },
                  identity: userLogin?.id || uuidv4(),
                }}
              >
                <NextNProgress color="#78909C" height={3} showOnShallow />
                {!isReadyToRender ? (
                  <Loader isLoading />
                ) : (
                  <>
                    {GTM_ID && (
                      <Script id="google-tag-manager" strategy="afterInteractive">
                        {`
                    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${GTM_ID}');
                  `}
                      </Script>
                    )}
                    <Component
                      {...pageProps}
                      router={router}
                      history={usePreviousRoute()}
                      featureFlags={flagsmithState?.flags}
                    />
                  </>
                )}
              </FlagsmithProvider>
            </ErrorBoundary>
          </AuthTokenProvider>
          <GlobalStyle isNotSafari={isNotSafari} isDisallowedSwitcher={isDisallowedSwitcher} />
        </RegionLanguageProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

App.getInitialProps = async ({ ctx }) => {
  const req = ctx?.req;
  const res = ctx?.res;
  if (req && res) {
    handleRedirects(ctx);
  }
  // eslint-disable-next-line no-console
  console.log('CTX URL::', ctx?.req?.url);
  console.log('CTX PATH::', ctx?.asPath);
  console.log('profile::', process.env.NEXT_PUBLIC_SENTRY_ENV);

  if (!ctx?.asPath?.includes('/sa-') && !ctx?.asPath?.includes('/uae-')) {
    return { flagsmithState: {}, selectedCountry: null };
  }
  
  const country = ctx?.asPath?.includes('/sa-') ? 'SA' : 'AE';
  
  if (
    process.env.NEXT_PUBLIC_SENTRY_ENV === 'PROD-F' ||
    process.env.NEXT_PUBLIC_SENTRY_ENV === 'PREPROD-F' ||
    process.env.NEXT_PUBLIC_SENTRY_ENV === 'PRODKSA-F'
  ) {
    await loadProdConfigs(country);
  }


  try {
    const identity = uuidv4();
    const traits = {
      country,
    };

    const apiTimeout = 5000;
    const initPromise = new Promise((resolve, reject) => {
      try {
        resolve(
          flagsmith.init({
            api: process.env.NEXT_PUBLIC_FLAGSMITH_API,
            environmentID: process.env.NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT,
            traits,
            identity
          }),
        );
      } catch (e) {
        logger.error('🚀 ~ file: _app.js:474 ~ initPromise ~ e:', stringifyIfObject(e));
        reject(e);
      }
    });

    await Promise.race([
      initPromise,
      // eslint-disable-next-line no-promise-executor-return
      new Promise((_, reject) => setTimeout(() => reject('API request timeout'), apiTimeout)),
    ]);

    // Uncomment below line if feature flags are needed as part of context. If performance is considered.
    // ctx.req.featureFlags = flagsmith?.getState()?.flags;
    return { flagsmithState: flagsmith?.getState() || {}, selectedCountry: country };
  } catch (e) {
    logger.error('🚀 ~ file: _app.js:487 ~ App.getInitialProps= ~ e:', stringifyIfObject(e));
    return { flagsmithState: {}, selectedCountry: '' };
  }
};

App.propTypes = {
  pageProps: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  Component: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  flagsmithState: PropTypes.object.isRequired,
};

export default wrapper.withRedux(App);

